import {
  Progress,
  ResponseErrorPanel,
  StatusWarning,
  Table,
  WarningPanel,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Typography } from '@material-ui/core';
import {
  formatDate,
  PackageSummary,
  PROJECT_SLUG_ANNOTATION,
  scopeLabel,
  usePackages,
} from './api';
import { FiltersOnRight } from './FiltersOnRight';
import { PackageStatus, statusLabel } from './PackageStatus';

type Row = PackageSummary & {
  id: string;
  statusLabel: string;
  versions: string;
  license: string;
  scopeLabel: string;
  unpinned: boolean;
  firstSeen: string | null;
};

/** Direct dependencies of the entity's repository, with their review status. */
export const EntityPackagesContent = () => {
  const { entity } = useEntity();
  const repo = entity.metadata.annotations?.[PROJECT_SLUG_ANNOTATION];
  const { value, loading, error } = usePackages(repo);

  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;

  const scan = value?.repos[0];
  if (!value || !scan) {
    return (
      <Typography>
        {repo} has not been scanned yet. Repositories are scanned in hourly
        batches, so this can take a few hours after a repository joins the
        catalog.
      </Typography>
    );
  }

  const rows: Row[] = value.packages.map(pkg => ({
    ...pkg,
    id: `${pkg.ecosystem}:${pkg.name}`,
    statusLabel: statusLabel(pkg.status),
    versions: pkg.usages[0].versions.join(', '),
    license: pkg.usages[0].licenses.join(', ') || '—',
    scopeLabel: scopeLabel(pkg.usages[0].scope),
    unpinned: pkg.usages[0].unpinned,
    firstSeen: pkg.usages[0].firstSeen,
  }));

  return (
    <>
      {scan.error && (
        <WarningPanel
          title="The latest scan failed; showing the last successful one"
          message={scan.error}
        />
      )}
      {value.registryError && (
        <WarningPanel
          title="Could not read the package registry"
          message={value.registryError}
        />
      )}
      <FiltersOnRight>
        <Table<Row>
          title={`Packages in ${repo}`}
          subtitle={`Scanned ${formatDate(scan.scannedAt)}`}
          options={{ pageSize: 25, padding: 'dense' }}
          data={rows}
          filters={[
            { column: 'Ecosystem', type: 'multiple-select' },
            { column: 'Used as', type: 'multiple-select' },
            { column: 'Status', type: 'multiple-select' },
          ]}
          columns={[
            { title: 'Package', field: 'name', highlight: true },
            { title: 'Ecosystem', field: 'ecosystem' },
            { title: 'Used as', field: 'scopeLabel' },
            {
              title: 'Versions',
              field: 'versions',
              render: row =>
                row.unpinned ? (
                  <StatusWarning>
                    {row.versions} (not pinned to a SHA)
                  </StatusWarning>
                ) : (
                  row.versions
                ),
            },
            {
              title: 'Licence',
              field: 'license',
              render: row =>
                row.copyleft ? (
                  <StatusWarning>{row.license} (copyleft)</StatusWarning>
                ) : (
                  row.license
                ),
            },
            {
              title: 'Status',
              field: 'statusLabel',
              render: row => <PackageStatus status={row.status} />,
            },
            { title: 'Capability', field: 'capability', emptyValue: '—' },
            { title: 'Notes', field: 'notes', emptyValue: '' },
            {
              title: 'Added',
              field: 'firstSeen',
              defaultSort: 'desc',
              render: row =>
                row.firstSeen ? formatDate(row.firstSeen) : 'Before first scan',
            },
          ]}
        />
      </FiltersOnRight>
    </>
  );
};
